import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AcademicCapIcon } from '@heroicons/react/24/outline'
import type { Application, QuizCategory, QuizDifficulty, QuizQuestion, QuizSession, SubscriptionPlan } from '../types'
import { FREE_TIER_LIMITS } from '../types'
import { generateQuiz, saveQuizSession, fetchUserQuizSessions, fetchUserQuizCount } from '../services/quizService'
import { UpgradePrompt } from './UpgradePrompt'
import { QuizExecutor } from './QuizExecutor'
import { formatDate } from '../utils/dateFormat'
import { PageLayout } from './PageLayout'
import type { DateFormat } from '../types'

type Props = {
  userId: string
  plan: SubscriptionPlan
  applications: Application[]
  dateFormat: DateFormat
}

const DIFFICULTY_OPTIONS: { value: QuizDifficulty; label: string }[] = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
]

const COUNT_OPTIONS = [5, 10, 20, 30]

const CATEGORY_OPTIONS: { value: QuizCategory; label: string; description: string }[] = [
  { value: 'technical', label: 'Technical', description: 'Tech stack questions' },
  { value: 'conceptual', label: 'Conceptual', description: 'Architecture & design' },
  { value: 'behavioral', label: 'Behavioral', description: 'Workplace scenarios' },
]

const difficultyColor: Record<string, string> = {
  easy: 'text-emerald-600 dark:text-emerald-400',
  medium: 'text-amber-600 dark:text-amber-400',
  hard: 'text-rose-600 dark:text-rose-400',
}

export function QuizPage({ userId, plan, applications, dateFormat }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preSelectedAppId = searchParams.get('app')

  // Quiz state
  const [isQuizActive, setIsQuizActive] = useState(false)
  const [generatedQuestions, setGeneratedQuestions] = useState<QuizQuestion[]>([])

  // Config state
  const [selectedAppId, setSelectedAppId] = useState<string>(preSelectedAppId || '')
  const [difficulty, setDifficulty] = useState<QuizDifficulty>('medium')
  const [questionCount, setQuestionCount] = useState(10)
  const [categories, setCategories] = useState<QuizCategory[]>(['technical', 'conceptual', 'behavioral'])

  // Loading/error
  const [isGenerating, setIsGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // History
  const [quizHistory, setQuizHistory] = useState<QuizSession[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const [quizCount, setQuizCount] = useState(0)

  // Sorted applications for dropdown
  const sortedApps = useMemo(() =>
    [...applications]
      .filter(a => !a.archived)
      .sort((a, b) => a.company.localeCompare(b.company)),
    [applications]
  )

  const selectedApp = useMemo(() =>
    applications.find(a => a.id === selectedAppId) || null,
    [applications, selectedAppId]
  )

  // Can create quiz?
  const canCreateQuiz = plan === 'pro' || quizCount < FREE_TIER_LIMITS.maxQuizzes
  const isLimitReached = !canCreateQuiz

  // Load history & count
  useEffect(() => {
    if (!userId) return
    setIsLoadingHistory(true)

    Promise.all([
      fetchUserQuizSessions(userId),
      fetchUserQuizCount(userId),
    ])
      .then(([sessions, count]) => {
        setQuizHistory(sessions)
        setQuizCount(count)
      })
      .catch(err => console.error('Failed to load quiz data:', err))
      .finally(() => setIsLoadingHistory(false))
  }, [userId])

  // Pre-select app from URL
  useEffect(() => {
    if (preSelectedAppId && sortedApps.some(a => a.id === preSelectedAppId)) {
      setSelectedAppId(preSelectedAppId)
    }
  }, [preSelectedAppId, sortedApps])

  const toggleCategory = (cat: QuizCategory) => {
    setCategories(prev => {
      if (prev.includes(cat)) {
        if (prev.length <= 1) return prev // min 1
        return prev.filter(c => c !== cat)
      }
      return [...prev, cat]
    })
  }

  const handleGenerate = async () => {
    if (!selectedApp) return
    if (categories.length === 0) return

    setIsGenerating(true)
    setGenerateError(null)

    try {
      const questions = await generateQuiz(
        selectedApp.positionTitle || `${selectedApp.company} position`,
        selectedApp.techStack,
        difficulty,
        questionCount,
        categories
      )
      setGeneratedQuestions(questions)
      setIsQuizActive(true)
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate quiz')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleFinishQuiz = async (answeredQuestions: QuizQuestion[]) => {
    const correctAnswers = answeredQuestions.filter(q => q.is_correct).length
    const score = Math.round((correctAnswers / answeredQuestions.length) * 100)

    try {
      const saved = await saveQuizSession(
        userId,
        selectedAppId || null,
        difficulty,
        categories,
        answeredQuestions,
        correctAnswers,
        score
      )
      setIsQuizActive(false)
      setGeneratedQuestions([])
      setQuizCount(prev => prev + 1)
      navigate(`/quiz/${saved.id}`)
    } catch (err) {
      console.error('Failed to save quiz:', err)
      // Still navigate, save error not critical for UX
      setIsQuizActive(false)
      setGeneratedQuestions([])
    }
  }

  const handleCancelQuiz = () => {
    setIsQuizActive(false)
    setGeneratedQuestions([])
  }

  // If quiz active, show executor
  if (isQuizActive && generatedQuestions.length > 0) {
    return (
      <div className="py-4">
        <QuizExecutor
          questions={generatedQuestions}
          onFinish={handleFinishQuiz}
          onCancel={handleCancelQuiz}
        />
      </div>
    )
  }

  return (
    <PageLayout
      icon={AcademicCapIcon}
      title="Interview Quiz"
      subtitle="AI-generated interview questions tailored to your applications"
    >
      {/* Limit reached - Upgrade prompt */}
      {isLimitReached && (
        <div className="card p-6">
          <UpgradePrompt type="feature" featureName="Interview Quizzes" />
        </div>
      )}

      {/* Start New Quiz */}
      {!isLimitReached && (
        <div className="card p-6 space-y-5">
          <h2 className="text-lg font-semibold text-app-ink font-display">Start New Quiz</h2>

          {/* Application select */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1.5">
              Application
            </label>
            <select
              value={selectedAppId}
              onChange={(e) => setSelectedAppId(e.target.value)}
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
            >
              <option value="">Select an application...</option>
              {sortedApps.map(app => (
                <option key={app.id} value={app.id}>
                  {app.company}{app.positionTitle ? ` — ${app.positionTitle}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty + Count */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">
                Difficulty
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as QuizDifficulty)}
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                {DIFFICULTY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">
                Number of Questions
              </label>
              <select
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value))}
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                {COUNT_OPTIONS.map(n => (
                  <option key={n} value={n}>{n} questions</option>
                ))}
              </select>
            </div>
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-2">
              Categories
            </label>
            <div className="flex flex-wrap gap-3">
              {CATEGORY_OPTIONS.map(cat => {
                const isChecked = categories.includes(cat.value)
                return (
                  <label
                    key={cat.value}
                    className={`flex items-center gap-2.5 rounded-lg border-2 px-4 py-2.5 cursor-pointer transition-all focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-app-accent ${
                      isChecked
                        ? 'border-app-accent bg-app-highlight'
                        : 'border-app-line hover:border-app-line'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleCategory(cat.value)}
                      className="sr-only"
                    />
                    <div>
                      <p className={`text-sm font-medium ${isChecked ? 'text-app-accent' : 'text-app-ink'}`}>
                        {cat.label}
                      </p>
                      <p className="text-xs text-app-muted">{cat.description}</p>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Error */}
          {generateError && (
            <div className="rounded-lg border border-rose-300 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
              {generateError}
            </div>
          )}

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={!selectedAppId || categories.length === 0 || isGenerating}
            className="flex items-center justify-center gap-2 w-full rounded-lg bg-app-primary px-5 py-3 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGenerating ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Generating questions...
              </>
            ) : (
              <>
                <AcademicCapIcon className="h-5 w-5" />
                Generate Quiz
              </>
            )}
          </button>

          {plan !== 'pro' && quizCount === 0 && (
            <p className="text-xs text-center text-app-muted">
              Your first quiz is free! Pro users get unlimited quizzes.
            </p>
          )}
        </div>
      )}

      {/* Quiz History */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-app-ink font-display">Quiz History</h2>

        {isLoadingHistory ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="card p-4 animate-pulse">
                <div className="h-5 w-48 bg-app-subtle rounded" />
                <div className="h-4 w-32 bg-app-subtle rounded mt-2" />
              </div>
            ))}
          </div>
        ) : quizHistory.length === 0 ? (
          <div className="card p-8 text-center">
            <AcademicCapIcon className="h-12 w-12 text-app-muted mx-auto mb-3" />
            <p className="text-sm text-app-muted">
              No quizzes yet. Start your first interview quiz above!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {quizHistory.map(session => {
              const app = applications.find(a => a.id === session.application_id)
              const scoreColor =
                session.score >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
                session.score >= 50 ? 'text-amber-600 dark:text-amber-400' :
                'text-rose-600 dark:text-rose-400'

              return (
                <Link
                  key={session.id}
                  to={`/quiz/${session.id}`}
                  className="card p-4 flex items-center gap-4 hover:border-app-line transition-colors group"
                >
                  {/* Score */}
                  <div className={`text-2xl font-bold ${scoreColor} w-16 text-center flex-shrink-0`}>
                    {session.score}%
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-ink truncate">
                      {app ? `${app.company}${app.positionTitle ? ` — ${app.positionTitle}` : ''}` : 'Deleted application'}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`text-xs font-medium ${difficultyColor[session.difficulty]}`}>
                        {session.difficulty.charAt(0).toUpperCase() + session.difficulty.slice(1)}
                      </span>
                      <span className="text-xs text-app-muted">
                        {session.correct_answers}/{session.total_questions} correct
                      </span>
                      <span className="text-xs text-app-muted">
                        {formatDate(session.created_at, dateFormat)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(session.categories as string[]).map(cat => (
                        <span
                          key={cat}
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-app-page text-app-muted"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Arrow */}
                  <svg className="h-5 w-5 text-app-muted group-hover:text-app-muted transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </PageLayout>
  )
}
